/**
 * Performance Monitoring System
 * 性能监控和报告系统
 */

export interface PerformanceMetrics {
  // Core Web Vitals
  FCP?: number; // First Contentful Paint
  LCP?: number; // Largest Contentful Paint
  FID?: number; // First Input Delay
  CLS?: number; // Cumulative Layout Shift
  TTFB?: number; // Time to First Byte

  // Additional metrics
  domContentLoaded?: number;
  loadComplete?: number;
  firstPaint?: number;
  firstByte?: number;

  // Custom metrics
  componentRenderTime?: Record<string, number>;
  apiResponseTime?: Record<string, number>;
  bundleSize?: number;
  memoryUsage?: number;
}

export interface PerformanceReport {
  timestamp: number;
  url: string;
  userAgent: string;
  metrics: PerformanceMetrics;
  deviceInfo: {
    screenSize: string;
    connection: string;
    deviceMemory?: number;
  };
}

/**
 * Performance Monitor Class
 */
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: PerformanceMetrics = {};
  private observers: PerformanceObserver[] = [];
  private reports: PerformanceReport[] = [];
  private isInitialized = false;

  private constructor() {}

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Initialize performance monitoring
   */
  init(): void {
    if (this.isInitialized) return;

    this.setupWebVitals();
    this.setupResourceTiming();
    this.setupNavigationTiming();
    this.setupMemoryMonitoring();

    this.isInitialized = true;

    if (import.meta.env.DEV) {
      console.log('🚀 Performance monitoring initialized');
    }
  }

  /**
   * Setup Core Web Vitals monitoring
   */
  private setupWebVitals(): void {
    // First Contentful Paint
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      this.metrics.FCP = lastEntry.startTime;
    });
    fcpObserver.observe({ entryTypes: ['paint'] });

    // Largest Contentful Paint
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      this.metrics.LCP = lastEntry.startTime;
    });
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        this.metrics.FID = entry.processingStart - entry.startTime;
      });
    });
    fidObserver.observe({ entryTypes: ['first-input'] });

    // Cumulative Layout Shift
    const clsObserver = new PerformanceObserver((list) => {
      let clsValue = 0;
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      this.metrics.CLS = clsValue;
    });
    clsObserver.observe({ entryTypes: ['layout-shift'] });

    this.observers.push(fcpObserver, lcpObserver, fidObserver, clsObserver);
  }

  /**
   * Setup resource timing monitoring
   */
  private setupResourceTiming(): void {
    const resourceObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: PerformanceEntry) => {
        if (entry.entryType === 'resource') {
          const resourceEntry = entry as PerformanceResourceTiming;
          // Monitor API calls
          if (resourceEntry.initiatorType === 'fetch' || resourceEntry.initiatorType === 'xmlhttprequest') {
            this.trackApiCall(resourceEntry.name, resourceEntry.responseEnd - resourceEntry.requestStart);
          }
        }
      });
    });
    resourceObserver.observe({ entryTypes: ['resource'] });
    this.observers.push(resourceObserver);
  }

  /**
   * Setup navigation timing monitoring
   */
  private setupNavigationTiming(): void {
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (navigation) {
        this.metrics.domContentLoaded = navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart;
        this.metrics.loadComplete = navigation.loadEventEnd - navigation.loadEventStart;
        this.metrics.firstByte = navigation.responseStart - navigation.requestStart;
        this.metrics.TTFB = navigation.responseStart - navigation.requestStart;
      }
    });
  }

  /**
   * Setup memory monitoring
   */
  private setupMemoryMonitoring(): void {
    // Monitor memory usage (Chrome only)
    if ('memory' in performance) {
      setInterval(() => {
        const memory = (performance as any).memory;
        this.metrics.memoryUsage = memory.usedJSHeapSize;
      }, 30000); // Every 30 seconds
    }
  }

  /**
   * Track component render time
   */
  trackComponentRender(componentName: string, renderTime: number): void {
    if (!this.metrics.componentRenderTime) {
      this.metrics.componentRenderTime = {};
    }
    this.metrics.componentRenderTime[componentName] = renderTime;
  }

  /**
   * Track API call performance
   */
  trackApiCall(endpoint: string, responseTime: number): void {
    if (!this.metrics.apiResponseTime) {
      this.metrics.apiResponseTime = {};
    }
    this.metrics.apiResponseTime[endpoint] = responseTime;
  }

  /**
   * Get current performance metrics
   */
  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  /**
   * Generate performance report
   */
  generateReport(): PerformanceReport {
    const report: PerformanceReport = {
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      metrics: this.getMetrics(),
      deviceInfo: {
        screenSize: `${window.screen.width}x${window.screen.height}`,
        connection: this.getConnectionType(),
        deviceMemory: (navigator as any).deviceMemory
      }
    };

    this.reports.push(report);
    return report;
  }

  /**
   * Get connection type
   */
  private getConnectionType(): string {
    const connection = (navigator as any).connection;
    if (connection) {
      return connection.effectiveType || 'unknown';
    }
    return 'unknown';
  }

  /**
   * Send report to monitoring service
   */
  async sendReport(report: PerformanceReport): Promise<void> {
    if (import.meta.env.DEV) {
      console.log('📊 Performance Report:', report);
      return;
    }

    try {
      // Send to analytics/monitoring service
      // Example: await fetch('/api/performance', { method: 'POST', body: JSON.stringify(report) });
      console.log('📊 Performance report sent');
    } catch (error) {
      console.error('Failed to send performance report:', error);
    }
  }

  /**
   * Get performance score based on Web Vitals
   */
  getPerformanceScore(): {
    score: number;
    grade: 'good' | 'needs-improvement' | 'poor';
    recommendations: string[];
  } {
    const recommendations: string[] = [];
    let score = 100;

    // LCP scoring
    if (this.metrics.LCP) {
      if (this.metrics.LCP > 4000) {
        score -= 30;
        recommendations.push('Improve Largest Contentful Paint (LCP)');
      } else if (this.metrics.LCP > 2500) {
        score -= 15;
        recommendations.push('Consider optimizing LCP');
      }
    }

    // FID scoring
    if (this.metrics.FID) {
      if (this.metrics.FID > 300) {
        score -= 30;
        recommendations.push('Improve First Input Delay (FID)');
      } else if (this.metrics.FID > 100) {
        score -= 15;
        recommendations.push('Consider optimizing FID');
      }
    }

    // CLS scoring
    if (this.metrics.CLS) {
      if (this.metrics.CLS > 0.25) {
        score -= 30;
        recommendations.push('Fix Cumulative Layout Shift (CLS)');
      } else if (this.metrics.CLS > 0.1) {
        score -= 15;
        recommendations.push('Consider reducing CLS');
      }
    }

    let grade: 'good' | 'needs-improvement' | 'poor';
    if (score >= 90) grade = 'good';
    else if (score >= 50) grade = 'needs-improvement';
    else grade = 'poor';

    return { score: Math.max(0, score), grade, recommendations };
  }

  /**
   * Cleanup observers
   */
  destroy(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.isInitialized = false;
  }
}

/**
 * Performance monitoring hook for React components
 * Note: Import React separately to avoid circular dependencies
 */
export function createPerformanceMonitorHook() {
  return function usePerformanceMonitor(componentName: string) {
    // This would need to be implemented as a separate hook file
    // to avoid circular dependencies with React imports
    const startTime = performance.now();

    const trackRender = () => {
      const renderTime = performance.now() - startTime;
      PerformanceMonitor.getInstance().trackComponentRender(componentName, renderTime);
    };

    return { trackRender };
  };
}

/**
 * Global performance monitor instance
 */
export const performanceMonitor = PerformanceMonitor.getInstance();

// Auto-initialize in browser environment
if (typeof window !== 'undefined') {
  // Delay initialization to ensure DOM is ready
  setTimeout(() => {
    performanceMonitor.init();
  }, 0);
}
